/**
 * The payer adjudicated the claim and refused it, so the biller corrects it
 * with a replacement claim version rather than resending the same document
 * (EMR-1253, EMR-1409). The tab carries no Reason column -- the payer's
 * reasons live on the status badge's popover (EMR-1268), and a second
 * surface would render the same reason twice.
 *
 * The tab classifies CORRECTION_DRAFT cycles too (eoe_claim_tabs maps that
 * status here): while the cycle sits in its correction-draft workflow state,
 * its open replacement draft is editable and can still be abandoned.
 */
import React, { useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@roger/ui-components';
import BillingPeriodCell from '../BillingPeriodCell';
import { getActiveBillingCycles } from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { BillingDataTable } from '../BillingDataTable';
import { BillingActionsCell } from '../BillingActionsCell';
import { ClaimWorksheetViewerAction } from '../ClaimWorksheetViewerAction';
import { ClaimTimelineAction } from '../ClaimTimeline';
import { ReplacementAction } from '../ReplacementAction';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { currencySortingFn } from '../billingTableUtils';
import { getClaimStatusBadge } from './claimStatusBadge';
import { PatientCell } from '../PatientCell';
import { InsuranceCell } from '../InsuranceCell';
import { PayerReasonBadge } from '../claimReason/PayerReasonBadge';

interface EOEDeniedTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEDeniedTab: React.FC<EOEDeniedTabProps> = ({ onCountsInvalidated }) => {
  const query = useBillingTableQuery('denied', {
    sortKeyByColumnId: { billing_period: 'billing_period_start' },
  });
  const { data, isLoading, isRefreshing, total, totalPages, loadData } =
    useBillingCycles<
      ActiveBillingCycleItem,
      { statuses: EpisodicClaimStatus[] }
    >({
      tabId: 'denied',
      fetchFn: getActiveBillingCycles,
      // CORRECTION_DRAFT classifies into this tab (eoe_claim_tabs), so the
      // row query must ask for both statuses or a cycle in its correction
      // draft vanishes from the manager.
      fetchPayload: {
        statuses: ['DENIED', 'CORRECTION_DRAFT'],
        ...query.toParams(),
      },
      errorMessage: 'Could not fetch data for the "Denied" tab.',
      getId: item => item.billing_cycle_id,
      getName: item => item.patient_name,
    });

  // EMR-1409: a correction draft can be approved to ready-to-send from its
  // worksheet. The refresh is the same one the pending-approval tab uses
  // after an approval: reload this tab's rows (the cycle leaves
  // CORRECTION_DRAFT, so it must leave the denied list) and then the pill
  // counts.
  const refreshAfterApproval = async () => {
    await loadData();
    await onCountsInvalidated?.();
  };

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <PatientCell
            name={row.original.patient_name}
            mrn={row.original.patient_mrn}
          />
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const badge = getClaimStatusBadge(row.original.billing_cycle_status);
          // While the cycle sits in CORRECTION_DRAFT, its open draft is a
          // replacement before approval, so the row reads "Replacement draft"
          // (the design vocabulary; never "Resubmitted"). In every other
          // state the version label comes from the current claim: a denied
          // cycle whose payer-facing claim is itself a replacement carries
          // "Replacement ready" / "Replacement submitted" here.
          const versionLabel =
            row.original.billing_cycle_status === 'CORRECTION_DRAFT'
              ? 'Replacement draft'
              : row.original.claim_version_label;
          return (
            <div className='flex min-w-0 flex-wrap items-center gap-1'>
              {badge ? (
                <PayerReasonBadge
                  kind='claim'
                  id={row.original.billing_cycle_id}
                  label={badge.label}
                  variant={badge.variant}
                  reasonCount={row.original.reason_count}
                  status={row.original.billing_cycle_status}
                  isRefusal={
                    row.original.billing_cycle_status !== 'CORRECTION_DRAFT'
                  }
                />
              ) : null}
              {versionLabel ? (
                <Badge
                  variant='outline'
                  className='whitespace-nowrap text-xs text-amber-700 border-amber-300 bg-amber-50'
                >
                  {versionLabel}
                </Badge>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        sortingFn: currencySortingFn,
      },
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.billing_period_end) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.billing_period_end}
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={
              <>
                <ClaimTimelineAction
                  billingCycle={row.original}
                  onApproved={refreshAfterApproval}
                />
                <ReplacementAction
                  billingCycleId={row.original.billing_cycle_id}
                  billingCycleStatus={row.original.billing_cycle_status}
                  claimCorrectionKind={
                    row.original.claim_correction_kind === 'resubmission'
                      ? 'resubmission'
                      : null
                  }
                  onDone={async () => {
                    // A replacement move changes the row (cycle status, label)
                    // and the tab counts, so both must reload.
                    await loadData();
                    await onCountsInvalidated?.();
                  }}
                />
                <ClaimWorksheetViewerAction
                  billingCycle={row.original}
                  onApproved={refreshAfterApproval}
                />
              </>
            }
          />
        ),
      },
    ],
    []
  );

  return (
    <BillingDataTable
      data={data}
      columns={columns}
      getRowId={row => row.billing_cycle_id}
      query={query}
      total={total}
      totalPages={totalPages}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
    />
  );
};

export default EOEDeniedTab;
